import { IconFolderOpenAngledOutline } from '@icons/IconFolderOpenAngledOutline'
import { IconSearchOutline } from '@icons/IconSearchOutline'
import { Badge } from '@/components/shadcn/badge'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { useCallback, useMemo, useState } from 'react'
import { ComponentHeader } from '../../shared/ComponentHeader'
import { ComponentTitle } from '../../shared/ComponentTitle'
import { SearchInput } from '../../shared/SearchInput'
import { IndexSelectorV2 } from '../IndexSelectorV2'
import { isUISchema, StatementEditorResponse } from '../types-v6'
import { ContentEditorActions } from './ContentEditorActions'
import { ItemColumnsLayout } from './ItemColumnsLayout'
import { ItemHeaderLayout } from './ItemHeaderLayout'
import { StatementEditorEmptyResponse } from '../StatementEditorEmptyResponse'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import clsx from 'clsx'
import { ComponentActionsContainer } from '../../shared/ComponentActionsContainer'
import { getComponentHeights } from '../../utils'

interface StatementEditorContentProps {
  data: StatementEditorResponse
  tileFilters?: React.ReactNode
}

export function StatementEditorContentV6({ data, tileFilters }: StatementEditorContentProps) {
  const { dataSchema, uiSchema } = data
  const { indexData, contentData } = dataSchema
  const { title, description, permissions, columns, fields, emphasizeHeader } = uiSchema
  const { canEditContent, canDeleteContent } = permissions
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const [selectedContentIds, setSelectedContentIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`

  const columnFields = fields.filter((ff) => ff.fieldConfig.layout.position === 'columns')
  const headerFields = fields.filter((ff) => ff.fieldConfig.layout.position === 'header')

  const handleContentSelection = useCallback((contentIds: string[]) => {
    setSelectedContentIds((prev) => [...prev, ...contentIds])
  }, [])

  const handleUnselectContent = useCallback((contentIds: string[]) => {
    setSelectedContentIds((prev) => prev.filter((id) => !contentIds.includes(id)))
  }, [])

  const selectedContent = useMemo(
    () => contentData.filter((content) => selectedContentIds.includes(content.id)),
    [contentData, selectedContentIds]
  )

  const filteredContent = useMemo(() => {
    if (!searchQuery) return selectedContent

    return selectedContent.filter((content) => {
      // Search helper function to recursively search through objects and arrays
      const searchInValue = (value: unknown, query: string): boolean => {
        // Handle strings
        if (typeof value === 'string') {
          return value.toLowerCase().includes(query.toLowerCase())
        }

        // Handle numbers
        if (typeof value === 'number') {
          return value.toString().toLowerCase().includes(query.toLowerCase())
        }

        // Handle arrays
        if (Array.isArray(value)) {
          return value.some((item) => searchInValue(item, query))
        }

        // Handle objects (excluding null)
        if (value !== null && typeof value === 'object') {
          return Object.values(value).some((v) => searchInValue(v, query))
        }

        return false
      }

      // Search across all fields in the content object
      return searchInValue(content, searchQuery)
    })
  }, [selectedContent, searchQuery])

  const noContentSelected = (
    <div className="flex w-full flex-col items-center justify-center gap-y-10 pt-36">
      <IconFolderOpenAngledOutline size={100} className="text-muted-foreground/50" />
      <div className="text-lg text-muted-foreground">No content selected</div>
    </div>
  )
  const noResultsFound = (
    <div className="flex w-full flex-col items-center justify-center gap-y-10 pt-36">
      <IconSearchOutline size={100} className="text-muted-foreground/50" />
      <div className="text-lg text-muted-foreground">No results found</div>
    </div>
  )

  if (!isUISchema(uiSchema)) {
    return <StatementEditorEmptyResponse />
  }

  return (
    // Component Container
    <div className="flex h-full flex-col">
      {hasHeader && (
        // ComponentHeader
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      {/* Index and Content Container */}
      <div style={{ height: contentHeight }} className="grid grid-cols-[max-content_1fr] gap-x-5 pt-5">
        {/* Index Container */}
        <ScrollArea className="bg-offwhite h-full rounded-lg p-5 shadow">
          <div>
            <IndexSelectorV2
              indexData={indexData}
              selectedContentIds={selectedContentIds}
              handleContentSelection={handleContentSelection}
              handleUnselectContent={handleUnselectContent}
            />
          </div>
        </ScrollArea>
        {/* Content Container */}
        <ScrollArea className="h-full w-full">
          <div className="relative flex w-full flex-col">
            {/* Content Header Container */}
            <div className="sticky top-0 flex w-full items-start justify-between border-b bg-background/50 px-2 py-1 backdrop-blur">
              <div className="text-lg font-bold">Content</div>
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            {/* Empty States */}
            {!selectedContentIds.length && noContentSelected}
            {selectedContentIds.length > 0 && filteredContent.length === 0 && noResultsFound}
            {/* Content Items */}
            {selectedContentIds.length > 0 &&
              filteredContent.map((content) => {
                return (
                  // Content Item Container
                  <div key={content.id} className="flex w-full flex-col gap-y-5 border-b pb-10">
                    {/* Content Item Header Container */}
                    <div className="flex items-center justify-between pr-2 pt-5">
                      <div className="flex flex-1 justify-between gap-x-2">
                        <div className="flex rounded-md bg-secondary/5 px-3 text-foreground shadow-sm">
                          {Array.isArray(content.position) && content.position.length > 0
                            ? content.position.map((position: string, index: number, self: string[]) => (
                                <div className="flex items-center">
                                  <div
                                    className="inline-flex h-full max-w-36 items-center text-xs font-light"
                                    key={position}
                                  >
                                    <span className="truncate">{position}</span>
                                  </div>
                                  <div
                                    className={clsx(
                                      'h-full px-3 text-foreground/50',
                                      index === self.length - 1 ? 'hidden' : ''
                                    )}
                                  >
                                    <IconRightChevronOutline className="size-full" />
                                  </div>
                                </div>
                              ))
                            : null}
                        </div>
                        <div className="flex gap-x-2">
                          <ItemHeaderLayout headerFields={headerFields} content={content} />
                        </div>
                      </div>
                      <ContentEditorActions
                        content={content}
                        canEditContent={canEditContent}
                        canDeleteContent={canDeleteContent}
                        uiSchema={uiSchema}
                        handlerId={uiSchema.handlerId}
                        // contentFormSchema={injectFormDefaults(contentFormSchema, content)}
                      />
                    </div>
                    {/* Content Item Body Container */}
                    <div style={{ gridTemplateColumns }} className="grid w-full gap-x-5">
                      <ItemColumnsLayout
                        columnFields={columnFields}
                        content={content}
                        canEditContent={canEditContent}
                        canDeleteContent={canDeleteContent}
                      />
                    </div>
                  </div>
                )
              })}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
